package com.hyperether.goodjob.scenes.components

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.snapshots.SnapshotStateList
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.input.pointer.PointerType
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.input.pointer.positionChange
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.hyperether.goodjob.getPlatformChecker


@Composable
fun <T> GenericListScreen(
    allItems: List<T>,
    items: List<T>,
    currentPage: MutableState<Int>,
    itemsPerPage: Int,
    selectedItems: SnapshotStateList<String>,
    saveSelectedItem: (String) -> Unit,
    getItemId: (T) -> String?,
    ItemRowHeader: @Composable (Boolean, (Boolean) -> Unit) -> Unit,
    ItemRow: @Composable (T, Boolean, (Boolean) -> Unit, Modifier) -> Unit,
    PaginationControls: @Composable (List<T>, Int, Int, (Int) -> Unit) -> Unit,
    modifier: Modifier = Modifier
) {
    val allSelected = remember(items, selectedItems) {
        derivedStateOf {
            items.isNotEmpty() && items.all { getItemId(it)?.let { id -> selectedItems.contains(id) } == true }
        }
    }

    Column(modifier = modifier.fillMaxSize()) {
        ItemRowHeader(
            allSelected.value
        ) { selectAll ->
            if (selectAll) {
                selectedItems.addAll(items.mapNotNull { getItemId(it) })
            } else {
                selectedItems.removeAll(items.mapNotNull { getItemId(it) })
            }
        }
        // Item list with LazyColumn
        val listState = rememberLazyListState()
        LazyColumn(
            state = listState,
            modifier = Modifier
                .verticalScrollbar(listState)
                .weight(1f)
                .disableMouseDragScrollOnWeb()
        ) {
            // Add item rows
            items(items.takeIf { it.isNotEmpty() } ?: emptyList()) { item ->
                val itemId = getItemId(item)
                if (itemId != null) {
                    ItemRow(item, selectedItems.contains(itemId), { isSelected ->
                        if (isSelected) {
                            selectedItems.add(itemId)
                        } else {
                            selectedItems.remove(itemId)
                        }
                    }, Modifier.clickableWithPointer {
                        saveSelectedItem(itemId)
                    })
                }
            }
        }

        // Pagination controls
        PaginationControls(
            allItems, currentPage.value, itemsPerPage
        ) { page -> currentPage.value = page }
    }
}

fun Modifier.disableMouseDragScrollOnWeb(
    verticalScrollbarWidth: Dp = 15.dp,
    horizontalScrollbarHeight: Dp = 15.dp,
): Modifier =
    if (!getPlatformChecker().isMobile()) {
        pointerInput(verticalScrollbarWidth, horizontalScrollbarHeight) {
            awaitPointerEventScope {
                val vBarWidthPx = verticalScrollbarWidth.toPx()
                val hBarHeightPx = horizontalScrollbarHeight.toPx()

                var pressedInsideScrollbar = false

                while (true) {
                    val event = awaitPointerEvent()

                    event.changes.forEach { change ->
                        if (change.type == PointerType.Mouse) {
                            when {
                                change.pressed && change.previousPressed -> {
                                    // Continue drag: consume only if not a scrollbar drag
                                    if (!pressedInsideScrollbar) {
                                        val delta = change.positionChange()
                                        if (delta != Offset.Zero) change.consume()
                                    }
                                }

                                change.pressed && !change.previousPressed -> {
                                    // Mouse just pressed: check if press is in scrollbar zones
                                    val pos = change.position
                                    val inVerticalBar = pos.x >= size.width - vBarWidthPx
                                    val inHorizontalBar = pos.y >= size.height - hBarHeightPx

                                    pressedInsideScrollbar = inVerticalBar || inHorizontalBar
                                }

                                !change.pressed && change.previousPressed -> {
                                    // Released — reset state
                                    pressedInsideScrollbar = false
                                }
                            }
                        }
                    }
                }
            }
        }
    } else this